import Seo from "../components/Seo";
import { MarketingHeader, CTASection } from "../components/MarketingShell";
import { FreeTrialText, PlanSummary } from "../components/PricingCopy";
import LegalFooter from "../components/LegalFooter";

const CATEGORIES = [
  {
    name: "General",
    items: [
      { q: "What is LinguaSub?", a: "LinguaSub is an AI subtitle studio that turns any video, audio or text in one of 21 supported languages into subtitles in another. It's built for creators, educators and localisation teams who care about tone, not just literal word-for-word accuracy." },
      { q: "Who is LinguaSub for?", a: "YouTubers, Instagram/TikTok creators, wedding videographers, journalists, film festival submitters, coaching institutes, EdTech teams, corporate L&D — anyone with video in one language and an audience in another." },
      { q: "Do I need to install anything?", a: "No. LinguaSub runs entirely in your browser. Chrome, Safari, Firefox, Edge and Brave are all supported. Mobile browsers work but editing is easier on a laptop." },
    ],
  },
  {
    name: "Languages",
    items: [
      { q: "Which languages are supported?", a: "21, any-to-any: Hindi, Bhojpuri, Magahi, Awadhi, Urdu, Tamil, Telugu, Bengali, Malayalam, Gujarati, Punjabi, English, Chinese, French, German, Russian, Japanese, Marathi, Spanish, Korean, Italian." },
      { q: "Can I go English → Hindi (not just Hindi → English)?", a: "Yes. All 21 languages are available on both source and target. Click the swap button between the two dropdowns to flip them in one tap." },
      { q: "Does it support Bhojpuri, Magahi, Awadhi?", a: "Yes, though Whisper's core models are strongest for standard Hindi. LinguaSub sends Whisper a dialect-aware prompt to bias the transcription, and GPT-5.4 handles dialect-specific expressions in translation. Clean audio gives the best results." },
      { q: "What about Hinglish (mixed Hindi + English)?", a: "Handled cleanly. We read the full transcript for context and translate each line to natural target-language English (or whatever target you picked), keeping proper nouns intact." },
    ],
  },
  {
    name: "Files & limits",
    items: [
      { q: "What file formats can I upload?", a: "Video: MP4, MOV, WEBM, MKV, AVI. Audio: MP3, WAV, M4A, OGG, FLAC. Text: paste directly into the text tab." },
      { q: "How big can my file be?", a: "Up to 5 GB per upload. Larger files are chunked internally so long clips still process." },
      { q: "How long can my video be?", a: "Your available minute balance applies. A 60-minute podcast uses 60 processing minutes. The free trial is one-time; paid packs add minutes without removing existing balances." },
      { q: "How long is my media stored?", a: "48 hours by default so you can edit against the video and re-render. After that, the original is deleted automatically. The SRT/VTT and job history stay in your account." },
    ],
  },
  {
    name: "Editing & output",
    items: [
      { q: "Can I edit the subtitles?", a: "Yes. Every video job opens an editor with the video player next to a segment list. Click any segment to pause the video and jump to it. Edit the translated text inline and adjust start/end. Save whenever you want." },
      { q: "Can I export a video with subtitles burnt in?", a: "Yes — click 'Render subtitled video' in the editor. LinguaSub runs ffmpeg to burn white-on-black-outline text into the frames and produces an MP4 you can post directly to YouTube, Reels, TikTok, or WhatsApp." },
      { q: "What subtitle formats do you export?", a: "SRT (SubRip, works everywhere) and VTT (WebVTT, ideal for web video players). Both are downloadable per job." },
      { q: "Can I style the burnt-in subtitles?", a: "Right now the burn-in uses a default readable style (white text, black outline, 22 px, 40 px bottom margin). A style picker is on the roadmap." },
    ],
  },
  {
    name: "Plans & billing",
    items: [
      { q: "Is there a free tier?", a: <><FreeTrialText />. It never resets or refills. Existing users keep their remaining free balance and do not receive another grant. No card required.</> },
      { q: "What do paid plans cost?", a: <PlanSummary /> },
      { q: "How do payments work?", a: "Cashfree checkout charges the server-verified INR price. Each purchase adds a prepaid minute pack with its own expiry; existing balances are preserved. Nothing renews or charges automatically." },
      { q: "Do you accept UPI / Indian cards?", a: "Cashfree checkout shows the payment methods available for your order, including supported Indian payment options." },
    ],
  },
  {
    name: "Privacy & rights",
    items: [
      { q: "Do you use my videos to train AI?", a: "No. LinguaSub does not train models on your content. Your media is transmitted to OpenAI (Whisper for transcription, GPT-5.4 for translation) for processing only — see their policies for their handling. We delete the original media from our servers after 48 hours." },
      { q: "Can I use LinguaSub output commercially?", a: "Yes on any paid plan. Free-tier output is fine for personal, educational, or open-source use. See /terms." },
      { q: "What if someone uses LinguaSub on my copyrighted video?", a: "File a takedown at /takedown. We investigate every notice and act on valid ones." },
    ],
  },
];

export default function FaqsPage() {
  return (
    <div className="grain-bg min-h-screen bg-zinc-950 text-white">
      <Seo path="/faqs" title="FAQs" description="Frequently asked questions about LinguaSub — languages, file limits, pricing, privacy, editing, subtitle formats, and how the automatic subtitle translation works." />
      <MarketingHeader />

      <section className="border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-16 md:py-20">
          <div className="text-[10px] font-mono uppercase tracking-widest text-blue-400 mb-3">Everything you might ask</div>
          <h1 className="font-heading text-4xl md:text-6xl font-black tracking-tighter leading-tight mb-4 text-white">
            FAQs.
          </h1>
          <p className="text-zinc-400 text-lg leading-relaxed">
            Six categories, real answers. Can't find yours? Post it on the feedback wall inside the app and we'll add it here.
          </p>
        </div>
      </section>

      {CATEGORIES.map((cat) => (
        <section key={cat.name} className="border-b border-zinc-800">
          <div className="max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-16">
            <div className="text-[10px] font-mono uppercase tracking-widest text-blue-400 mb-3">{cat.name}</div>
            <div className="divide-y divide-zinc-800 border-t border-zinc-800">
              {cat.items.map((it) => (
                <details key={it.q} className="group py-5">
                  <summary className="cursor-pointer list-none flex items-start justify-between gap-4 text-white">
                    <span className="text-base md:text-lg font-semibold pr-4">{it.q}</span>
                    <span className="text-zinc-500 text-xl group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="text-sm text-zinc-400 leading-relaxed mt-3">{it.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      ))}

      <CTASection title="Ready to try LinguaSub?" subtitle={<><FreeTrialText />. Sign in with Google or email.</>} />
      <div className="max-w-6xl mx-auto px-4 md:px-8"><LegalFooter /></div>
    </div>
  );
}
